import { validate as validatePostalCode } from 'postal-codes-js';
import { isEmpty } from 'ramda';

import { parsePhone } from '@/utils/phone';

import { ERROR_MESSAGES, EXTENDED_LATIN_CHARACTERS_REGEXP } from '@/constants/shipping';

import { COUNTRY_ISO, COUNTRY_STATES } from '@/constants/countries';

import { findDifferentField } from '@/utils/dictionaire';

const createValidator = (validationFunction, errorMessage) => ({
  validate: validationFunction,
  errorMessage
});

const createValidators = countryISO => ({
  IS_LATIN: createValidator(
    value => value === '' || !!EXTENDED_LATIN_CHARACTERS_REGEXP.test(value),
    ERROR_MESSAGES.LATIN_CHARACTERS_ERROR
  ),
  ZIP_CODE: createValidator(
    value => value === '' || validatePostalCode(countryISO, value) === true,
    ERROR_MESSAGES.INCORRECT_ZIP_CODE
  ),
  REQUIRED: createValidator(value => !!value, ERROR_MESSAGES.REQUIRED),
  PHONE_NUMBER: createValidator(value => {
    if (countryISO.toLowerCase() === COUNTRY_ISO.SAUDI_ARABIA.toLowerCase()) {
      const { isValid } = parsePhone(countryISO, value);

      return isValid;
    }

    return !!/^\d*$/.test(value);
  }, ERROR_MESSAGES.VALID_PHONE_NUMBER),
  DIGITS: createValidator(value => !!/^\d*$/.test(value), ERROR_MESSAGES.DIGITS_ONLY),
  LENGTH_GREATER_OR_EQUAL_12: createValidator(
    value => (value ? value.length >= 12 : true),
    ERROR_MESSAGES.REQUIRED_MIN_LENGTH
  )
});

const validateField = (fieldValue, fieldName, validators) => {
  const { errorMessage } = validators.find(({ validate: isValid }) => !isValid(fieldValue)) || {};

  if (errorMessage) {
    return { fieldName, errorMessage };
  }

  return null;
};

const validateFields = (fields, validators) => {
  const errorsList = validators
    .map(({ fieldName, validators: fieldValidators }) => {
      const value = fields[fieldName];

      return validateField(value, fieldName, fieldValidators);
    })
    .filter(error => !!error);

  const isValidTab = isEmpty(errorsList);

  const errors = errorsList.reduce((acc, { fieldName, errorMessage }) => {
    acc[fieldName] = errorMessage;

    return acc;
  }, {});

  return { isValidTab, errors };
};

const isCountryWithRegions = countryIso => !!COUNTRY_STATES[countryIso.toUpperCase()];

const findFieldValidators = (fieldName, allValidators) => {
  const { validators } = allValidators.find(field => field.fieldName === fieldName) || {
    validators: []
  };

  return validators;
};

/** Pick the one field that changed and apply validations for the field */
const getUpdatedErrors = ({ previousData, newData, allValidators, currentErrors }) => {
  const changedField = findDifferentField(previousData, newData);

  if (!changedField) {
    return currentErrors;
  }

  const { fieldName, value } = changedField;

  const validators = findFieldValidators(fieldName, allValidators);

  const { errorMessage } = validateField(value, fieldName, validators) || {};

  if (errorMessage) {
    return {
      ...currentErrors,
      [fieldName]: errorMessage
    };
  }

  return {
    ...currentErrors,
    [fieldName]: null
  };
};

const getFullShippingAddress = (shippingData, isSaudiArabia) => {
  const { address, apartment, city, zipCode, district, building, street, additionalNumber } =
    shippingData || {};

  if (isSaudiArabia) {
    return [building, street, '-', district, city, zipCode, '-', additionalNumber]
      .filter(value => !!value)
      .join(' ');
  }

  const apartmentPart = apartment ? `${apartment},` : '';

  return [apartmentPart, address, zipCode, city].filter(value => !!value).join(' ');
};

export {
  validateFields,
  validateField,
  createValidators,
  isCountryWithRegions,
  findFieldValidators,
  getUpdatedErrors,
  getFullShippingAddress
};
